import React, { useEffect, useMemo, useState } from "react";
import HoverPreview from "./components/HoverPreview/HoverPreview";
import SocialLinks from "./components/SocialLinks/SocialLinks";

// --- Theme helpers ---------------------------------------------------------
// Respect system preference on first load; persist user choice afterwards.
// Sources: MDN prefers-color-scheme, WCAG color contrast, ARIA Switch guidance.
// https://developer.mozilla.org/en-US/docs/Web/CSS/@media/prefers-color-scheme
// https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum
// https://www.w3.org/WAI/ARIA/apg/patterns/switch/

type Theme = "light" | "dark";

function getSystemTheme(): Theme {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function getInitialTheme(): Theme {
  try {
    const saved = localStorage.getItem("theme");
    if (saved === "light" || saved === "dark") return saved as Theme;
  } catch { }
  return getSystemTheme();
}

export default function PortfolioApp() {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  // Apply data-theme on <html> and persist
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    try { localStorage.setItem("theme", theme); } catch { }
  }, [theme]);

  // Keyboard + click accessible switch
  const toggle = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  const themeLabel = useMemo(() => (theme === "dark" ? "Dark" : "Light"), [theme]);

  return (
    <div className="min-h-screen bg-bg text-tx transition-colors duration-300">
      <GlobalStyles />

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur supports-[backdrop-filter]:bg-bg/80 border-b border-br">
        <nav className="max-w-6xl mx-auto px-4 md:px-6 flex items-center gap-3 h-16">
          <a href="#home" className="font-extrabold tracking-tight select-none">Ian Bareham</a>
          <div className="ml-auto flex items-center gap-1">
            <a className="navlink" href="#about">About</a>
            <a className="navlink" href="#work">Work</a>
            <a className="navlink" href="#games">Games</a>
            <a className="navlink" href="#music">Music</a>
            <a className="navlink" href="#contact">Contact</a>

            {/* Theme switch (ARIA switch pattern) */}
            <div className="w-px h-5 mx-2 bg-br hidden sm:block" aria-hidden="true" />
            <button
              type="button"
              role="switch"
              aria-checked={theme === "dark"}
              aria-label="Toggle dark mode"
              className="inline-flex items-center gap-2 rounded-full border border-br px-3 py-1.5 text-sm hover:bg-su focus:outline-none focus:ring-2 focus:ring-ac/50"
              onClick={toggle}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }}
            >
              <span className="inline-flex h-4 w-4 items-center justify-center">
                {/* Icon glyphs via emoji for zero-deps simplicity */}
                <span aria-hidden>{theme === "dark" ? "🌙" : "☀️"}</span>
              </span>
              <span className="text-muted">{themeLabel}</span>
            </button>
          </div>
        </nav>
      </header>

      <main id="home" className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Hero */}
        <section className="py-12 md:py-16 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h1 className="text-3xl md:text-5xl font-extrabold leading-tight mb-3">
              Senior Software Engineer · Creative Technologist · Musician · Indie Game Developer
            </h1>
            <p className="text-muted mb-6">
              A highly experienced and creative senior software engineer with 20+ years delivering impactful solutions across healthcare, finance, digital agencies, and gaming. I lead and collaborate on full‑stack development, cloud architecture, and front‑end innovation. Outside of commercial roles I design 2D Unity games and compose/produce music — a creative lens that shapes my approach to software design, UX, and collaboration.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="#work" className="btn btn-primary">See Professional Work</a>
              <a href="#music" className="btn">Hear My Music</a>
            </div>
            <SocialLinks
              className="mt-4"
              iconSize={22}
              white={false}   // inherits theme text color so it works in light & dark
              links={{
                linkedin: "https://www.linkedin.com/in/ian-bareham-60366943/",
                github: "https://github.com/ianbara",
                email: "ian@ianbareham.co.uk",
                // facebook: "#",
                // instagram: "#",
                // website: "#",   // your music site or personal site
                spotify: "https://open.spotify.com/artist/6EUcFBV2cBNPJRfvLuNVdw?si=QoQudaYtRlm7FXBh3AzPAQ",
              }}
            />
          </div>
          <div className="card min-h-[220px] flex items-center justify-center">
            <div className="opacity-70">
              <img src="https://www.famemagazine.co.uk/wp-content/uploads/2024/06/Ian-Bareham-7a.jpg" />
            </div>
          </div>
        </section>

        {/* About */}
        <section id="about" className="py-12 md:py-16 border-t border-br">
          <h2 className="section-title">About</h2>
          <p className="section-intro">What I bring: pragmatic engineering, clear UX thinking, and creative instincts from games & music.</p>
          <div className="card p-5">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-semibold mb-2">Technical Skills</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Languages:</strong> JavaScript, TypeScript, C#, PHP, Node.js</li>
                  <li><strong>Frontend:</strong> React, Preact, Vue.js, Angular, HTML5, CSS/Sass, Storybook</li>
                  <li><strong>Backend & Frameworks:</strong> .NET (MVC/Web API), REST APIs, Express, Razor, Azure Functions</li>
                  <li><strong>Cloud & DevOps:</strong> AWS, Azure, Kubernetes, Jenkins, Terraform, Docker</li>
                  <li><strong>Databases:</strong> SQL Server, Cosmos DB, DynamoDB, MySQL, Entity Framework</li>
                  <li><strong>Messaging:</strong> Kafka, RabbitMQ, Kafka Connect</li>
                  <li><strong>CMS:</strong> Umbraco (Certified), Kentico (Certified), WordPress, Orchard, Prismic, Contentful</li>
                  <li><strong>Testing:</strong> Jest, Selenium, TDD/BDD, NUnit, Moq</li>
                  <li><strong>Tools:</strong> Git, VSCode, Visual Studio, Grafana, Prometheus, WebPageTest</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Creative</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Games:</strong> Unity (2D)</li>
                  <li><strong>Visuals:</strong> Photoshop (prototyping & art)</li>
                  <li><strong>Music:</strong> Studio One, EZDrummer, Audacity — songwriting, recording, live performance</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Projects with filter */}
        <ExperienceSection />

        <ProjectsSection />

        {/* Games */}
        <section id="games" className="py-12 md:py-16 border-t border-br">
          <h2 className="section-title">Games / Personal Projects</h2>
          <p className="section-intro">Short, visual project cards with quick links to play or view code.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <ProjectCard
              title="A Viking's Rage"
              meta="Unity (C#), Photoshop · 2025"
              desc="Viking themed platformer game. Character design and animations."
              // poster="https://www.nintendo.com/eu/media/images/10_share_images/games_15/nintendo_switch_download_software_1/H2x1_NSwitchDS_TankBattleRetro_image1600w.jpg"
              poster="/images/VikingsRage_Screenshot.png"
              mp4Src="/media/vikings_rage.mp4"
              actions={[{ label: "View Show Reel", href: "https://youtu.be/jG9WxECqveA?si=_BIgGGqxAk558YOL" }]}
            />
            <ProjectCard title="Ninja Rumber" meta="Unity (C#), Photoshop · 2025" desc="Solo platformer. Character design and animations."
              poster="/images/ninja_rumble_screenshot.png"
              mp4Src="/media/ninja_rumble.mp4" actions={[{ label: "View Show Reel", href: "https://youtu.be/jG9WxECqveA?si=_BIgGGqxAk558YOL" }]} />
            <ProjectCard title="Tankers: A Quest for sound"
              poster="/images/rock_tankers_screenshot5.png"
              mp4Src="/media/rock_tankers_clip5.mp4"
              meta="Unity (C#), Unity Gaming Services, Photoshop · 2025" desc="Top‑Down Tank Battle (Single/Multiplayer)." actions={[{ label: "View Show Reel", href: "https://youtu.be/jG9WxECqveA?si=_BIgGGqxAk558YOL" }]} />
            <ProjectCard title="Mr Goo!" meta="Unity (C#), Photoshop ·2025" desc="Quick action mobile game. Character design and animations."
              poster="/images/mr_goo_screenshot.png"
              mp4Src="/media/mr_goo_clip.mp4" actions={[{ label: "View Show Reel", href: "https://youtu.be/jG9WxECqveA?si=_BIgGGqxAk558YOL" }]} />
            <ProjectCard title="Hit me Up - Fitness" meta="React Native App · 2024" desc="Mobile fitness APP, plan custom workout to your music. Developed in React Native, Typescript and EXPO."
              poster="/images/hitmeup_screenshot2.png"
              mp4Src="/media/hitmeup_short_clip.mp4" actions={[{ label: "View Show Reel", href: "https://youtu.be/jG9WxECqveA?si=_BIgGGqxAk558YOL" }]} />
            <ProjectCard title="Tune Go!" meta="React Native App · 2024" desc="The gigging musicians perfect app! Easy read lyrics and chords to aid performances."
              poster="/images/tunego_screenshot.png"
              actions={[{ label: "View Show Reel", href: "https://youtu.be/jG9WxECqveA?si=_BIgGGqxAk558YOL" }]} />
          </div>
        </section>

        {/* Music */}
        <section id="music" className="py-12 md:py-16 border-t border-br">
          <h2 className="section-title">Music & Gigs</h2>
          <p className="section-intro">Releases and live shows.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="card p-4">
              <span className="badge">Release</span>
              <h3 className="mt-2 font-semibold">“Call Me in the Morning”</h3>
              <p className="text-muted text-sm mb-2">Single · 2025</p>
              <div className="media">
                <img src="/images/callmeinthemorning.jpg" alt="Call Me in the Morning single cover" />
              </div>
              <p className="mt-2">Available Now on all streaming platforms.</p>
              <div className="flex gap-2 mt-2"><a className="btn" href="https://open.spotify.com/track/6ZnldyaKZFArIi0p8GV9eO?si=29774a93b8304ab5">Listen</a>
                {/* <a className="btn" href="#">Lyrics</a> */}
              </div>
            </div>
            {/* <div className="card p-4">
              <span className="badge">Live</span>
              <h3 className="mt-2 font-semibold">In‑store Acoustic Showcase — HMV Bradford</h3>
              <p className="text-muted text-sm mb-2">Saturday · 1:00 PM</p>
              <div className="media">
                <img src="/images/ib_hmv.png" alt="Ian Bareham Live in HMV Bradford" />
              </div>
              <p className="mt-2">Acoustic set highlighting storytelling and dynamics. PA‑friendly rig.</p>
              <div className="flex gap-2 mt-2"><a className="btn" href="#">RSVP</a><a className="btn" href="#">Watch</a></div>
            </div> */}
            <div className="card p-4">
              <span className="badge">Website</span>
              <h3 className="mt-2 font-semibold">Official Website</h3>
              <p className="text-muted text-sm mb-2">Ian Bareham Music</p>
              {/* <div className="media" aria-hidden /> */}
              <div className="media">
                <img src="/images/ib_website.png" alt="Ian Bareham Official Website" />
              </div>
              <p className="mt-2">Official website for singer songwritter Ian Bareham.</p>
              <div className="flex gap-2 mt-2"><a className="btn" href="https://www.ianbareham.co.uk">Visit</a></div>
            </div>
          </div>
        </section>

        {/* Contact */}
        {/* <section id="contact" className="py-12 md:py-16 border-t border-br">
          <h2 className="section-title">Let’s connect</h2>
          <p className="section-intro">For engineering roles, collaborations, gigs, or soundtrack work.</p>
          <form className="card p-5 max-w-2xl" action="#" method="post" aria-describedby="form-hint">
            <p id="form-hint" className="text-muted text-sm mb-2">This demo form doesn’t submit. Wire up your backend or a form service.</p>
            <div className="grid md:grid-cols-2 gap-3">
              <label className="grid gap-1">
                <span>Name</span>
                <input className="input" name="name" autoComplete="name" required />
              </label>
              <label className="grid gap-1">
                <span>Email</span>
                <input className="input" type="email" name="email" autoComplete="email" required />
              </label>
            </div>
            <label className="grid gap-1 mt-3">
              <span>Message</span>
              <textarea className="input min-h-[140px]" name="message" required />
            </label>
            <button className="btn btn-primary mt-4" type="submit">Send</button>
          </form>
        </section> */}
      </main>

      <footer id="contact" className="border-t border-br py-8 text-muted">
        <div className="max-w-6xl mx-auto px-4 md:px-6 flex flex-wrap items-center justify-between gap-3">
          <small>© {new Date().getFullYear()} Ian Bareham — Software Engineer · Games · Music</small>
          <nav aria-label="Secondary" className="flex gap-3">
            <a className="navlink" href="#about">About</a>
            <a className="navlink" href="#work">Work</a>
            <a className="navlink" href="#games">Games</a>
            <a className="navlink" href="#music">Music</a>
            <a className="navlink" href="#contact">Contact</a>
          </nav>
          <SocialLinks
            className="mt-3"
            iconSize={22}
            white={false}
            links={{
              linkedin: "https://www.linkedin.com/in/ian-bareham-60366943/",
              github: "https://github.com/ianbara",
              email: "ian@ianbareham.co.uk",
              // facebook: "#",
              // instagram: "#",
              // website: "#",   // your music site or personal site
              spotify: "https://open.spotify.com/artist/6EUcFBV2cBNPJRfvLuNVdw?si=QoQudaYtRlm7FXBh3AzPAQ",
            }}
          />
        </div>
      </footer>
    </div>
  );
}

// --- Experience Section -------------------------------------------------------
function ExperienceSection() {
  // Dispatch a custom event to set filters in ProjectsSection
  const filterTo = (detail: any) => () => {
    window.dispatchEvent(new CustomEvent('setProjectFilters', { detail }));
    // jump to projects section
    document.querySelector('#work')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section id="experience" className="py-12 md:py-16 border-t border-br">
      <h2 className="section-title">Experience</h2>
      <p className="section-intro">Where I worked and what I delivered. Click a chip to filter the projects below.</p>

      <div className="grid gap-4">
        {/* Flutter UKI — Sky Betting & Gaming */}
        <article className="card p-4">
          <h3 className="font-semibold">Flutter UKI — Sky Betting & Gaming</h3>
          <div className="text-muted text-sm">Senior Software Engineer (Jun 2022–Present) · Software Engineer (Jun 2021–Jun 2022)</div>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Shipped high‑traffic promos (Prize Machine, The Vault, Going for Goals) in React/TypeScript.</li>
            <li>3‑month cover for Engineering Manager: planning, stakeholder updates, 1:1s, coordination.</li>
            <li>Built shared UI components (React + Storybook); CMS features and performance dashboards.</li>
            <li>Improved web performance with Lighthouse/WebPageTest; mentored juniors & supported hiring.</li>
            <li>Earlier: features on Sky Vegas Portal (Next.js, React, Redux, TDD) & analytics experiments.</li>
          </ul>
          <div className="flex flex-wrap gap-2 mt-3">
            <button className="tab" onClick={filterTo({ type: 'pro', company: 'Sky Betting & Gaming' })}>Filter: Sky Betting & Gaming</button>
          </div>
        </article>

        {/* Contract Frontend Developer (2019–2020) */}
        <article className="card p-4">
          <h3 className="font-semibold">Sky Betting & Gaming - Contract Frontend Developer</h3>
          <div className="text-muted text-sm">2019–2020</div>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Built web notification service UI/services (Preact, Node.js, AWS); event‑driven systems (Kafka).</li>
            <li>Delivered A/B tooling, loading screens, and developer QA tools.</li>
          </ul>
          <div className="flex flex-wrap gap-2 mt-3">
            <button className="tab" onClick={filterTo({ type: 'pro', company: 'Digital Bear - Consulting' })}>Filter: Digital Bear - Consulting</button>
          </div>
        </article>

        {/* Intercept IP */}
        <article className="card p-4">
          <h3 className="font-semibold">Intercept IP — Contract Software Engineer</h3>
          <div className="text-muted text-sm">2018–2019</div>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Secure documents app & microservices (.NET Core, EF Core, RabbitMQ, Docker, Azure).</li>
            <li>Consultancy/dev across web apps & CMS (React, Vue, Angular, Umbraco Cloud).</li>
          </ul>
          <div className="flex flex-wrap gap-2 mt-3">
            <button className="tab" onClick={filterTo({ type: 'pro', company: 'Digital Bear - Consulting' })}>Filter: Digital Bear - Consulting</button>
          </div>
        </article>

        {/* Provident Financial */}
        <article className="card p-4">
          <h3 className="font-semibold">Provident Financial — Development Lead</h3>
          <div className="text-muted text-sm">2014–2018</div>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Led Satsuma Loans platform (.NET MVC, Azure, Umbraco) with 3rd‑party integrations.</li>
            <li>Mobile‑first optimization, performance & SEO improvements; introduced BDD/TDD.</li>
          </ul>
          <div className="flex flex-wrap gap-2 mt-3">
            <button className="tab" onClick={filterTo({ type: 'pro', company: 'Provident Financial' })}>Filter: Provident</button>
          </div>
        </article>

        {/* Earlier Roles */}
        <article className="card p-4">
          <h3 className="font-semibold">Earlier Roles</h3>
          <div className="text-muted text-sm">2000–2014</div>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Delivered EPOS, CRM, CMS, and back‑office systems with C#/.NET & SQL Server.</li>
            <li>Tenet Group, Woven (formerly Ink Digital), McCann, Soft Options (NHS supplier).</li>
          </ul>
        </article>
      </div>
    </section>
  );
}

// --- Projects Section with filter
function ProjectsSection() {
  type Filter = "all" | "pro" | "game" | "music";
  const [filter, setFilter] = useState<Filter>("all");
  const [company, setCompany] = useState<string>("all");

  // Listen for Experience chips (e.g., { type: 'pro', company: 'Digital Bear - Consulting' })
  useEffect(() => {
    const handler = (e: any) => {
      const d = e.detail || {};
      if (d.type) setFilter(d.type);
      if (d.company) setCompany(d.company);
    };
    window.addEventListener('setProjectFilters', handler as EventListener);
    return () => window.removeEventListener('setProjectFilters', handler as EventListener);
  }, []);

  const tabs: { key: Filter; label: string }[] = [
    { key: "all", label: "All" },
    { key: "pro", label: "Professional" },
    { key: "game", label: "Games" },
    { key: "music", label: "Music" },
  ];

  const items = [
    // --- Professional (Sky Betting & Gaming / Flutter UKI) ---
    { tag: "pro", company: "Sky Betting & Gaming", poster: "/images/info_newfeatures.png", title: "Prize Machine — Various Campaigns", meta: "React · TypeScript · 2022–2025", desc: "Contributed to Prize Machine campaigns, developing mini-games and new features to drive traffic and engagement. Worked with shared UI components and implemented robust state management for high-traffic promotional gameplay.", actions: [] },
    { tag: "pro", company: "Sky Betting & Gaming", poster: "/images/info_gaming2.png", title: "The Vault — Campaign", meta: "React · TypeScript · 2023", desc: "Built the “Hearts Lives” feature for special limited campaign — players earned collectible lives each week to increase prize win chances — while gamifying promotions, tuning performance, and aligning a cross-brand UI library.", actions: [] },
    { tag: "pro", company: "Sky Betting & Gaming", poster: "/images/info_sports.png", title: "Sporting Events — Campaigns", meta: "React · TypeScript · 2024", desc: "Developed UI components to showcase teams and horses for the Going for Goals / Cheltenham campaigns, boosting promotional appeal and customer engagement. Collaborated closely with UX and product teams, and implemented complex game-schedule mechanics.", actions: [] },
    // { tag: "pro", company: "Sky Betting & Gaming", poster: "/images/info_next.png", title: "Sky Vegas Portal Features", meta: "Next.js · React · Redux · 2021–2022", desc: "Portal features with TDD; experiments with BA/BI and Adobe Analytics.", actions: [] },
    { tag: "pro", company: "Sky Betting & Gaming", poster: "/images/info_performance2.png", title: "Performance Improvements", meta: "Preact · WebPack · WebPageTest · HTML · SASS · 2025", desc: "Built tools to monitor and report website performance across multiple brands, delivering performance enhancements and load-time improvements that drove better user experiences.", actions: [] },

    // --- Professional (Consulting / Contracts) ---
    { tag: "pro", company: "Digital Bear - Consulting", poster: "/images/info_notification2.png", title: "Notification Service (Web & Mobile)", meta: "Preact · Node.js · AWS · 2019–2020", desc: "Developed a scalable, high-traffic message notification service using a lightweight Preact + TypeScript UI, Kafka-driven event streaming, and AWS Lambda-based backend for performance and scalability..", actions: [] },
    { tag: "pro", company: "Digital Bear - Consulting", poster: "/images/info_secure.png", title: "Secure Documents Platform", meta: ".NET Core · EF Core · RabbitMQ · Azure · 2018–2019", desc: "Microservices + frontend for secure document handling; Dockerized deployments.", actions: [] },
    { tag: "pro", company: "Digital Bear - Consulting", poster: "/images/info_cms.png", title: "Content Management Integration & Web Design", meta: ".NET Core · EF Core · RabbitMQ · Azure · 2018–2019", desc: "Microservices + frontend for secure document handling; Dockerized deployments.", actions: [] },

    // --- Professional (Provident) ---
    {
      tag: "pro", company: "Provident Financial", poster: "/images/info_financial2.png", title: "Satsuma Loans Platform", meta: ".NET MVC · Azure · Umbraco · 2014–2018", desc: "Led development, integrations (Experian, Call Credit, Worldpay), mobile‑first perf & SEO; introduced BDD/TDD.",
      //actions: [{ label: "Summary", href: "#" }] },
      actions: []
    },

    // --- Professional (INK digital) ---
    { tag: "pro", company: "Woven (formerly Ink Digital)", poster: "/images/info_ink.png", title: "Housing Web Application", meta: "Javascript · Goolge Maps · Kentico· 2014", desc: "Sole developer of a multi-region property search web app, integrating Google Maps with a CMS-driven backend and working closely with designers and clients to deliver a polished, user-centric interface.", actions: [] },



    // // --- Games / Music (Personal) ---
    // { tag: "game", company: "Personal", title: "Top‑Down Tank Battle (Multiplayer)", meta: "Unity (C#), Netcode · 2025", desc: "Server‑auth projectiles, coin economy, and clean solo/network separation.", poster: "https://www.nintendo.com/eu/media/images/10_share_images/games_15/nintendo_switch_download_software_1/H2x1_NSwitchDS_TankBattleRetro_image1600w.jpg", mp4Src: "/media/tank-battle-preview.mp4", actions: [{ label: "Play demo", href: "#" }, { label: "GitHub", href: "#" }] },
    // {
    //   tag: "music", company: "Music", poster: "/images/callmeinthemorning.jpg", title: "Single: “Call Me in the Morning”", meta: "Release: 27 Sep 2025", desc: "Indie‑folk single; wrote, performed, and produced guitars and arrangements.",
    //   // actions: [{ label: "Listen", href: "#" },{ label: "Press kit", href: "#" }]
    //   actions: [],
    // },
    // { tag: "music", company: "Music", poster: "/images/callmeinthemorning.jpg", title: "Live @ Wallers Brewery, Sunbridgewells", meta: "Bradford · 9:00 PM set", desc: "Solo set featuring originals and indie‑folk covers; electric & acoustic guitar.", actions: [{ label: "Watch", href: "#" }] },
  ] as const;

  const visibleByType = items.filter(i => filter === 'all' || i.tag === filter);
  const companyOptions = Array.from(new Set(visibleByType.map(i => i.company)));
  const visible = visibleByType.filter(i => (company === 'all' || i.company === company));

  return (
    <section id="work" className="py-12 md:py-16 border-t border-br">
      <h2 className="section-title">Projects</h2>
      <p className="section-intro">Filter by type and company to see relevant highlights.</p>

      {/* Type tabs */}
      {/* TODO look at category tabs again */}
      {/* <div role="tablist" aria-label="Project type filters" className="flex flex-wrap gap-2 mb-3">
        {tabs.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={filter === t.key}
            className={`tab ${filter === t.key ? "tab-active" : ""}`}
            onClick={() => { setFilter(t.key); setCompany('all'); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setFilter(t.key); setCompany('all'); } }}
          >
            {t.label}
          </button>
        ))}
      </div> */}

      {/* Company chips (contextual to selected type) */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button className={`tab ${company === 'all' ? 'tab-active' : ''}`} onClick={() => setCompany('all')}>All companies</button>
        {companyOptions.map(c => (
          <button key={String(c)} className={`tab ${company === c ? 'tab-active' : ''}`} onClick={() => setCompany(String(c))}>{String(c)}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" aria-live="polite">
        {visible.map((i, idx) => (
          <ProjectCard key={idx} title={i.title} meta={i.meta} poster={i.poster} desc={i.desc} actions={i.actions} company={i.company as string} />
        ))}
      </div>
    </section>
  );
}

// --- Lazy hover preview (IntersectionObserver) ----------------------------
function LazyHoverPreview({ poster, mp4Src, webmSrc, alt }: { poster: string; mp4Src: string; webmSrc?: string; alt: string }) {
  const [visible, setVisible] = React.useState(false);
  const ref = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!ref.current || typeof IntersectionObserver === 'undefined') {
      // Fallback: if no IO, just render immediately
      setVisible(true);
      return;
    }
    const node = ref.current;
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
          break;
        }
      }
    }, { root: null, rootMargin: '25% 0px', threshold: 0 });
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className="hover-preview relative overflow-hidden rounded-xl">
      {!visible || !mp4Src ? (
        <img src={poster} alt={alt} className="w-full block" loading="lazy" />
      ) : (
        <HoverPreview poster={poster} mp4Src={mp4Src} webmSrc={webmSrc} alt={alt} />
      )}
    </div>
  );
}

function CardImage({ poster, alt }: { poster: string; alt: string }) {
  const ref = React.useRef<HTMLDivElement | null>(null);

  return (
    <div ref={ref} className="hover-preview relative overflow-hidden rounded-xl">
      <img src={poster} alt={alt} className="w-full block" loading="lazy" />
    </div>
  );
}

// --- Project Card ----------------------------------------------------------
function ProjectCard({ title, meta, desc, actions, company, poster, mp4Src, webmSrc }: { title: string; meta: string; desc: string; company?: string; poster?: string; mp4Src?: string; webmSrc?: string; actions: ReadonlyArray<{ label: string; href: string }> }) {
  return (
    <article className="card p-3">
      <div className="media" aria-hidden>
        {poster && mp4Src ? (
          <LazyHoverPreview poster={poster} mp4Src={mp4Src} alt={`${title} gameplay preview`} webmSrc={webmSrc} />
        ) : null}
        {poster && !mp4Src ? (
          <CardImage poster={poster} alt={`${title} gameplay preview`} />
        ) : null}
      </div>
      <h3 className="font-semibold mt-2">{title}</h3>
      <div className="text-muted text-sm mb-1">{meta}</div>
      {company && <div className="badge mb-2" aria-label="Company">{company}</div>}
      <p className="mb-2">{desc}</p>
      <div className="flex flex-wrap gap-2">
        {actions.map((a, i) => (
          <a key={i} className="btn" href={a.href} aria-label={`${a.label} for ${title}`}>{a.label}</a>
        ))}

        {!actions.length && (<div className="btn-placeholder"></div>)}
      </div>
    </article>
  );
}

// --- Global styles & tokens (two themes) -----------------------------------
function GlobalStyles() {
  return (
    <style>{`
      /* Color tokens for both themes. We toggle [data-theme] on <html>. */
      :root{ --bg:#ffffff; --su:#f6f7f9; --tx:#0f172a; --muted:#475569; --br:#e5e7eb; --ac:#2563eb; --ac-ct:#ffffff; --shadow:0 10px 30px rgba(2,6,23,.08);} /* light base */
      html[data-theme='dark']{ --bg:#0b1020; --su:#10172a; --tx:#e5e7eb; --muted:#94a3b8; --br:#1f2a44; --ac:#60a5fa; --ac-ct:#0b1020; --shadow:0 10px 30px rgba(0,0,0,.6); }

      /* Lighter variant palette (off-white) when body has .variant-soft */
      body.variant-soft{ --bg:#f8f9fb; --su:#ffffff; --tx:#0b0f19; --muted:#5b6472; --br:#e7e9ee; --ac:#1f4ed8; --ac-ct:#ffffff; }

      .bg-bg{ background:var(--bg); }
      .text-tx{ color:var(--tx); }
      .text-muted{ color:var(--muted); }
      .border-br{ border-color:var(--br); }
      .card{ background:var(--su); border:1px solid var(--br); border-radius:16px; box-shadow:var(--shadow); }
      .media{ aspect-ratio:16 / 9; border-radius:12px; background:linear-gradient(180deg, rgba(0,0,0,.08), rgba(0,0,0,.02)); }

      .btn{ display:inline-flex; align-items:center; gap:.5rem; padding:.6rem .9rem; border-radius:999px; border:1px solid var(--br); text-decoration:none; background:var(--su); }
      .btn-primary{ background:var(--ac); color:var(--ac-ct); border-color:transparent; }

      .btn-placeholder{ display:inline-flex; align-items:center; gap:.5rem; padding:.6rem .9rem; }

      .badge{ display:inline-block; font-size:12px; padding:.2rem .5rem; border:1px solid var(--br); border-radius:999px; color:var(--muted); background:var(--su); }
      .navlink{ padding:.5rem .75rem; border-radius:10px; color:var(--muted); text-decoration:none; }
      .navlink:hover{ color:var(--tx); background:color-mix(in oklab, var(--su) 85%, transparent); }

      .input{ width:100%; padding:.75rem 1rem; border:1px solid var(--br); border-radius:12px; background:var(--bg); color:var(--tx); }

      .section-title{ font-size:clamp(22px,3vw,30px); margin:0 0 8px; font-weight:700; }
      .section-intro{ color:var(--muted); margin:0 0 16px; }

      .tab{ padding:.5rem .75rem; border:1px solid var(--br); background:var(--su); border-radius:999px; cursor:pointer; }
      .tab-active{ background:var(--ac); color:var(--ac-ct); border-color:transparent; }

      @media (prefers-reduced-motion: reduce){ *{ animation-duration:.001ms !important; transition-duration:.001ms !important; scroll-behavior:auto !important; } .hover-preview video{ display:none; } }

/* Hover video reveal (works when hover is supported) */
.hover-preview video{ position:absolute; inset:0; width:100%; height:100%; object-fit:cover; opacity:0; pointer-events:none; transition:opacity .2s ease; }
@media (hover:hover) and (pointer:fine){
  .hover-preview:hover video, .hover-preview:focus-within video{ opacity:1; }
}
    `}</style>
  );
}
